/**
 * OPS-MASTER-002 — master-data module access resolution (separate from operational runtime).
 */
import {
  MASTER_MODULES,
  MASTER_MODULE_IDS,
  MASTER_PERMISSION_LEVELS,
} from './masterGovernanceRegistry.js';
import { canonicalPermissionKey, normKey } from './operationalModuleAliases.js';
import { permissionKeysMatch } from './permissionKeyMatching.js';
import { extractAppsPrefix } from './governance/tenantGovernanceIdentity.js';
import { UI_SHELL } from './workflowRoutes.js';

const MASTER_DATA_IDS = MASTER_MODULE_IDS.filter(
  (id) => MASTER_MODULES[id].governanceCategory === 'MASTER_DATA',
);

function pathMatchesPattern(pathname, pattern) {
  const p = String(pathname ?? '').trim();
  const pat = String(pattern ?? '').trim();
  if (!pat) return false;
  if (pat.endsWith('/*')) {
    const prefix = pat.slice(0, -2);
    return p === prefix || p.startsWith(`${prefix}/`);
  }
  if (pat.includes('*')) {
    const escaped = pat.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\\\.\*/g, '.*');
    return new RegExp(`^${escaped}$`).test(p);
  }
  const escaped = pat.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped}$`).test(p);
}

function anyPathMatch(pathname, patterns) {
  if (!pathname || !patterns?.length) return false;
  return patterns.some((pat) => pathMatchesPattern(pathname, pat));
}

/** Route patterns for HMS shell guard — includes nested paths under canonical bridge route. */
function routePatternsForModule(mod, shell) {
  const patterns = [...(mod.routePatterns ?? [])];
  const shellRoute = mod.routes?.[shell];
  if (shellRoute) {
    patterns.push(shellRoute);
    if (!shellRoute.endsWith('/*')) {
      patterns.push(`${shellRoute}/*`);
    }
  }
  return [...new Set(patterns.filter(Boolean))];
}

function anyUriMatch(uriList, patterns) {
  if (!Array.isArray(uriList) || !uriList.length || !patterns?.length) return false;
  return uriList.some((uri) => patterns.some((pat) => pathMatchesPattern(uri, pat)));
}

/** Aligned with resolveOperationalAccess — tenant-safe permissionKeysMatch + level suffixes. */
function permissionMatch(permissions, keys = [], prefixes = []) {
  if (!permissions?.length) return false;
  for (const k of keys) {
    if (permissions.some((p) => permissionKeysMatch(k, p))) return true;
    const appsPrefix = extractAppsPrefix(k);
    if (appsPrefix && /^(FONE|CHERITTO|REG[0-9A-Z]+)$/i.test(appsPrefix)) {
      continue;
    }
    const canonical = canonicalPermissionKey(k);
    if (canonical !== k && permissions.some((p) => permissionKeysMatch(canonical, p))) {
      return true;
    }
  }
  for (const prefix of prefixes) {
    const canonicalPrefix = canonicalPermissionKey(prefix);
    if (
      permissions.some(
        (p) => permissionKeysMatch(prefix, p) || permissionKeysMatch(canonicalPrefix, p),
      )
    ) {
      return true;
    }
    const px = normKey(prefix);
    if (!px) continue;
    for (const p of permissions) {
      if (normKey(p).includes(px)) return true;
    }
  }
  return false;
}

function masterAccessControlMatch(accessControlList, keys = []) {
  if (!keys.length || !Array.isArray(accessControlList) || !accessControlList.length) return false;
  return accessControlList.some((entry) => {
    const page = entry?.pageName ?? entry?.page_name;
    const type = String(entry?.accessType ?? entry?.access_type ?? '').toLowerCase();
    if (type !== 'read' && type !== 'read/write') return false;
    return keys.some((k) => permissionKeysMatch(k, page));
  });
}

function masterModuleKeys(mod) {
  return [
    ...(mod.permissionKeys ?? []),
    ...(mod.uriId ? [mod.uriId] : []),
    ...(mod.compatibilityPermissionKeys ?? []),
  ];
}

function masterGrantMatch(mod, ctx) {
  const keys = masterModuleKeys(mod);
  const prefixes = mod.permissionPrefixes ?? (mod.uriId ? [mod.uriId] : []);
  if (permissionMatch(ctx.permissions, keys, prefixes)) return true;
  if (masterAccessControlMatch(ctx.accessControlList, keys)) return true;
  const patterns = routePatternsForModule(mod, ctx.shell ?? UI_SHELL.HMS_SHELL);
  if (anyUriMatch(ctx.uriList, patterns)) return true;
  return false;
}

function resolveMasterModule(mod, ctx) {
  if (ctx.grantAll) return true;
  return masterGrantMatch(mod, ctx);
}

/** All route patterns for MASTER_DATA modules in a shell (menu + route guard parity). */
export function masterDataPathPatterns(shell = UI_SHELL.HMS_SHELL) {
  return MASTER_DATA_IDS.flatMap((id) => routePatternsForModule(MASTER_MODULES[id], shell));
}

export function isMasterDataPath(pathname, shell = UI_SHELL.HMS_SHELL) {
  return anyPathMatch(pathname, masterDataPathPatterns(shell));
}

/**
 * @param {object} ctx
 * @param {string[]} [ctx.permissions]
 * @param {string[]} [ctx.uriList] provisioned URI paths from grant_role (legacy menu URLs)
 * @param {Array} [ctx.accessControlList] page-level ACL rows (tenant uriId page names)
 * @param {boolean} [ctx.grantAll]
 * @param {string} [ctx.shell]
 * @param {string} [ctx.pathname] optional path for route-scoped checks
 */
export function resolveMasterAccess(ctx = {}) {
  const permissions = Array.isArray(ctx.permissions) ? ctx.permissions.filter(Boolean) : [];
  const uriList = Array.isArray(ctx.uriList) ? ctx.uriList.filter(Boolean) : [];
  const accessControlList = ctx.accessControlList ?? [];
  const grantAll = Boolean(ctx.grantAll);
  const shell = ctx.shell ?? UI_SHELL.HMS_SHELL;
  const moduleCtx = { permissions, uriList, accessControlList, grantAll, shell, pathname: ctx.pathname };

  const modules = {};
  for (const id of MASTER_DATA_IDS) {
    modules[id] = resolveMasterModule(MASTER_MODULES[id], moduleCtx);
  }

  const allowedRoutes = MASTER_DATA_IDS.filter((id) => modules[id])
    .map((id) => MASTER_MODULES[id].routes?.[shell])
    .filter(Boolean);

  return {
    modules,
    allowedRoutes,
    canAccessMasterModule: (moduleId) => Boolean(modules[moduleId]),
    canAccessPath: (pathname) => {
      if (grantAll) return true;
      return MASTER_DATA_IDS.some((id) => {
        const mod = MASTER_MODULES[id];
        if (!anyPathMatch(pathname, routePatternsForModule(mod, shell))) {
          return false;
        }
        return masterGrantMatch(mod, { ...moduleCtx, pathname });
      });
    },
    supportedLevels: MASTER_PERMISSION_LEVELS,
  };
}
